import {useQuery} from '@tanstack/react-query';
import {useConfig} from '@lore/config';

/**
 * @typedef {Object} EvaluationQuestionResponse
 * @property {string} response
 * @property {number} count
 */

/**
 * @typedef {Object} EvaluationQuestionGroup
 * @property {string} question
 * @property {EvaluationQuestionResponse[]} responses
 */

/**
 * @typedef {Object.<string, EvaluationQuestionGroup[]>} EvaluationQuestionsByTable
 */

/**
 * dummy export to allow JSDoc types to be imported by other files
 * @type {EvaluationQuestionResponse}
 */
export const _EvaluationQuestionResponse = {};

/**
 * dummy export to allow JSDoc types to be imported by other files
 * @type {EvaluationQuestionGroup}
 */
export const _EvaluationQuestionGroup = {};

const STALE_TIME = 15 * 60 * 1000; // 15 minutes

/**
 * Groups the flat evaluation questions data by table, then by question.
 *
 * @param {Array<Object>} data - Flat array of evaluation question response counts.
 * @returns {EvaluationQuestionsByTable} Data grouped by table and question.
 */
function groupByTableAndQuestion(data = []) {
  return data.reduce((acc, item) => {
    const {table, question, response, count} = item;

    if (!acc[table]) acc[table] = [];

    let questionGroup = acc[table].find((group) => group.question === question);
    if (!questionGroup) {
      questionGroup = {question, responses: []};
      acc[table].push(questionGroup);
    }

    questionGroup.responses.push({response, count});

    return acc;
  }, {});
}

/**
 * Custom hook to fetch evaluation questions from the core API.
 *
 * @param {Object} [params={}] - Query parameters for the request.
 * @param {number} [params.page=1] - The page number to fetch.
 * @param {number} [params.pageSize=500] - The number of items per page.
 * @param {number} [params.networkId] - Filter results by network ID.
 * @param {string} [params.startDate] - Filter results by start date (ISO date-time string).
 * @param {string} [params.endDate] - Filter results by end date (ISO date-time string).
 * @param {Object} [options={}] - Configuration options like 'enabled' to control the query.
 * @returns {import('@tanstack/react-query').UseQueryResult<EvaluationQuestionsByTable>} React Query result object.
 */
export default function useCoreEvaluationQuestions(
  {page = 1, pageSize = 500, networkId, startDate, endDate} = {},
  {enabled = true} = {}
) {
  const config = useConfig();
  const {apiRoot, headers} = config.connections.default;

  const query = useQuery({
    queryKey: ['evaluations', 'questions', page, pageSize, networkId, startDate, endDate],

    queryFn: () => {
      const url = new URL(`${apiRoot}/core/evaluations/questions`);

      // Add query parameters
      if (page != null) url.searchParams.append('page', String(page));
      if (pageSize != null) url.searchParams.append('pageSize', String(pageSize));
      if (networkId != null) url.searchParams.append('networkId', String(networkId));
      if (startDate != null) url.searchParams.append('startDate', String(startDate));
      if (endDate != null) url.searchParams.append('endDate', String(endDate));

      return fetch(url.toString(), {
        method: 'GET',
        headers: headers(),
      }).then((res) => {
        if (!res.ok) {
          throw new Error('Failed to fetch evaluation questions');
        }
        return res.json();
      }).then((json) => groupByTableAndQuestion(json.data));
    },

    staleTime: STALE_TIME,
    enabled,
  });

  return {
    ...query,
  };
}
